import { config as defaultConfig } from '../config.ts';
import type { Config } from '../config.ts';
import type { CourseProgress, HistoryEntry, Outcome, Progress } from '../types.ts';
import { addDays } from './dates.ts';
import type { LessonV2 } from './levels.ts';
import { blockPercent } from './percent.ts';
import { pruneLog } from './progressMerge.ts';
import { defaultReviewConfig, recordLesson } from './review.ts';
import type { ReviewConfig } from './review.ts';
import { newItem, review } from './srs.ts';
import type { ReviewPolicy } from './srs.ts';

export const STORAGE_KEY = 'naative.progress.v1';

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

export function emptyProgress(): Progress {
  return { version: 1, lastLessonDate: null, streak: 0, stars: 0, history: [] };
}

function defaultStore(): KeyValueStore | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // np. tryb prywatny / zablokowane dane witryn
  }
}

/** `key`: domyślnie postęp lokalny (tryb bez serwera); profile dzieci mają własne klucze (src/lib/profileStore.ts). */
export function loadProgress(store: KeyValueStore | null = defaultStore(), key: string = STORAGE_KEY): Progress {
  if (!store) return emptyProgress();
  try {
    const raw = store.getItem(key);
    if (!raw) return emptyProgress();
    const p = JSON.parse(raw) as Partial<Progress> & { completedDays?: unknown; items?: unknown };
    if (!p || typeof p !== 'object' || p.version !== 1) return emptyProgress();
    // dawne pola starego kursu „dni” (patch-49) wypadają przy wczytaniu, a z pamięci przeglądarki znikają przy najbliższym zapisie
    const { completedDays: _days, items: _items, ...rest } = p;
    return { ...emptyProgress(), ...rest, version: 1 } as Progress;
  } catch {
    return emptyProgress();
  }
}

export function saveProgress(p: Progress, store: KeyValueStore | null = defaultStore(), key: string = STORAGE_KEY): boolean {
  if (!store) return false;
  try {
    store.setItem(key, JSON.stringify(p));
    return true;
  } catch {
    return false;
  }
}

export interface ApplyCourseArgs {
  lesson: Pick<LessonV2, 'id' | 'targets'>;
  outcomes: readonly Outcome[];
  today: string;
  cfg?: Config;
}

/**
 * Czysta funkcja: postęp po ukończonej lekcji kursu v2 (SPEC §23.14). Lekcja trafia do `course.done`, jej cele do `course.items` (pudełko 1, powtórka jutro —
 * powtarza je „Powtórka dnia”, `applyReviewSession`) i do dziennika dnia `course.log` (patch-47), gwiazdki za wymowę (zdania i „Co powiesz?”) dolicza się
 * do ogólnej sumy, seria liczy się jak dotąd (kolejne dni z lekcją).
 */
export function applyCourseSession(prev: Progress, args: ApplyCourseArgs): Progress {
  const cfg = args.cfg ?? defaultConfig;
  const { today, lesson, outcomes } = args;
  const course: CourseProgress = { done: { ...(prev.course?.done ?? {}) }, items: { ...(prev.course?.items ?? {}) } };
  // dziennik dnia: cele lekcji dopisujemy tylko przy PIERWSZYM ukończeniu — powtórzenie lekcji z wyboru (lista w „Dla rodzica”) nie jest nauką nowych słów, więc nie może
  // zawyżać dziennego limitu powtórek ani zmieniać puli „wczorajszych” słów; z dziennika „Powtórka dnia” czyta pulę świeżą i tempo nauki
  course.log = prev.course?.done[lesson.id] === undefined ? pruneLog(recordLesson(prev.course?.log ?? {}, today, lesson.targets.map((t) => t.word))) : (prev.course?.log ?? {});
  // cele lekcji wchodzą do pudełka 1; do statystyki słowa trafia wynik jego luki (krok 5). Pytanie kroku 4 (`quiz`) wprowadza słowo, więc pomyłka przy pierwszym
  // spotkaniu ze słowem nie liczy się jako błąd — nie wpływa na statystykę słowa ani na „wpisane” w podsumowaniu (SPEC §23.26)
  for (const t of lesson.targets) {
    if (!course.items[t.word]) course.items[t.word] = newItem(t.word, today, cfg.srs);
  }
  for (const o of outcomes) {
    if (o.kind !== 'cloze' || o.review) continue;
    const item = course.items[o.ref];
    if (item && item.lastSeen === today && item.correct + item.wrong === 0) {
      course.items[o.ref] = { ...item, correct: item.correct + (o.correct ? 1 : 0), wrong: item.wrong + (o.correct ? 0 : 1) };
    }
  }
  const stars = outcomes.filter((o) => o.kind === 'speak' || o.kind === 'say').reduce((a, o) => a + (o.stars ?? 0), 0);
  const percent = blockPercent(outcomes);
  course.done[lesson.id] = { date: today, stars, ...(percent !== null ? { percent } : {}) };
  const next: Progress = { ...prev, history: [...prev.history], course };
  if (prev.lastLessonDate !== today) next.streak = prev.lastLessonDate === addDays(today, -1) ? prev.streak + 1 : 1;
  next.lastLessonDate = today;
  next.stars = prev.stars + stars;
  const cloze = outcomes.filter((o) => o.kind === 'cloze');
  const entry: HistoryEntry = {
    date: today,
    mode: 'lesson',
    lesson: lesson.id,
    stars,
    speakTasks: outcomes.filter((o) => o.kind === 'speak' || o.kind === 'say').length,
    clozeCorrect: cloze.filter((o) => o.correct).length,
    clozeTotal: cloze.length,
  };
  if (percent !== null) entry.percent = percent;
  next.history.push(entry);
  if (next.history.length > 200) next.history = next.history.slice(-200);
  return next;
}

export interface ApplyReviewArgs {
  outcomes: readonly Outcome[];
  today: string;
  cfg?: Config;
  /** Polityka powtórek (błąd = spadek o `lapseDrop` pudełek, rosnące odstępy pudełka 5); domyślnie z `defaultReviewConfig`. */
  policy?: ReviewPolicy;
}

/** Polityka powtórek z konfiguracji algorytmu (SPEC §23.13.2 pkt 6). */
export function reviewPolicyOf(cfg: ReviewConfig = defaultReviewConfig): ReviewPolicy {
  return { lapseDrop: cfg.lapseDrop, box5: cfg.box5 };
}

/**
 * Czysta funkcja: postęp po „Powtórce dnia” kursu v2 (patch-47, SPEC §23.27). Każda odpowiedź (pytanie do obrazka, luka, „Co powiesz?”) przesuwa słowo w `course.items`
 * przez `srs.review` z polityką: dobrze = wyższe pudełko i dłuższy odstęp, źle = spadek o 2 pudełka (min. 1). „Dobrze” to odpowiedź bez pomyłki i bez podpowiedzi za
 * pierwszym razem (`Outcome.correct`). Powtórka **nie daje gwiazdek** (ranking liczy tylko lekcje), nie zmienia serii ani daty ostatniej lekcji i **nie zostawia wpisu
 * w historii** (historia ma limit 200 wpisów, a czyta ją tylko kalendarz „Mój tydzień” — liczy w niej dni z lekcją — i ranking — gwiazdki; wpisy powtórek wypychałyby
 * lekcje z kalendarza). Pomijamy słowo spoza `course.items` (np. pozostałość po usuniętej lekcji) oraz słowo, które dziś już przesunęło się w tym postępie
 * (`lastSeen` = dziś i termin w przyszłości — np. drugie urządzenie powtórzyło je w trakcie tej sesji): nie przesuwamy go drugi raz tego samego dnia.
 */
export function applyReviewSession(prev: Progress, args: ApplyReviewArgs): Progress {
  const cfg = args.cfg ?? defaultConfig;
  const policy = args.policy ?? reviewPolicyOf();
  const course: CourseProgress = { ...(prev.course ?? { done: {}, items: {} }), done: { ...(prev.course?.done ?? {}) }, items: { ...(prev.course?.items ?? {}) } };
  const seen = new Set<string>();
  for (const o of args.outcomes) {
    if (o.kind !== 'quiz' && o.kind !== 'cloze' && o.kind !== 'say') continue;
    const item = course.items[o.ref];
    if (!item || seen.has(o.ref)) continue; // jedno słowo = jedna odpowiedź w sesji; drugi wynik nie przesuwa słowa drugi raz
    seen.add(o.ref);
    if (item.lastSeen === args.today && item.due > args.today) continue; // dziś już przesunięte (np. przez drugie urządzenie)
    course.items[o.ref] = review(item, o.correct === true, args.today, cfg.srs, policy);
  }
  return { ...prev, history: [...prev.history], course };
}
